import { useEffect } from "react"
import { useDispatch } from "react-redux"
import { showToastMessage } from "../../../stateManagement/reducers"
import SmallLoader from "../../loaders/SmallLoader"
import { useMapNavigation } from "./hooks/useMapNavigation"
import { useMapSearchLogic } from "./hooks/useMapSearchLogic"
import MapSearchbarResult from "./MapSearchbarResult"
import { clearSearchMapArtifacts } from "./hooks/searchMapState"

const clearIconSrc = require("../../../assets/images/fontAwesomeIcons/xmark-solid.svg").default

export default function MapSearchbar() {
    const dispatch = useDispatch()

    const {
        searchInputValue,
        setSearchInputValue,
        searchQuery,
        setSearchQuery,
        combinedResults,
        isResultsVisible,
        setIsResultsVisible,
        focusedResultIndex,
        setFocusedResultIndex,
        isSearching,
        localMapExtent
    } = useMapSearchLogic()

    const {
        isPathSelected,
        setIsPathSelected,
        selectedPathMeta,
        setSelectedPathMeta,
        pathPositionInputValue,
        setPathPositionInputValue,
        pathPositionQuery,
        setPathPositionQuery,
        isFetchingWfs,
        goToSearchResult: navGoToSearchResult,
        goToPath,
        goToPositionOnSelectedPath,
        normalizePathPositionInput
    } = useMapNavigation()

    // trigger search on path position change
    useEffect(() => {
        if (pathPositionQuery) {
            ; (async () => {
                try {
                    await goToPositionOnSelectedPath({ setSearchInputValue })
                } catch (error: any) {
                    dispatch(showToastMessage({ messageText: error.message, messageType: "error", windowType: "map" }))
                }
            })()
        }
    }, [pathPositionQuery])

    // Keyboard navigation
    useEffect(() => {
        if (focusedResultIndex != null) {
            if (focusedResultIndex >= 0 && isResultsVisible) {
                const focusedEl: any = document.getElementById("map-searchbar-item-focused")
                const searchResult = combinedResults[focusedResultIndex]
                focusedEl?.focus()
                
                // Navigate without clicking (just preview)
                navGoToSearchResult(searchResult, localMapExtent, {
                    setSearchInputValue,
                    setIsResultsVisible,
                    afterMouseClick: false
                })
            } else if (focusedResultIndex === -1) {
                const inputEl: any = document.getElementById("map-searchbar-input")
                inputEl?.focus()
                setIsResultsVisible(true)
            } else if (focusedResultIndex === -2) {
                const kmInputEl: any = document.getElementById("map-path-position-input")
                kmInputEl?.focus()
            }
        }
    }, [focusedResultIndex])


    function handleResultClick(searchResult: any, index: number) {
        setFocusedResultIndex(index)
        navGoToSearchResult(searchResult, localMapExtent, {
            setSearchInputValue,
            setIsResultsVisible,
            afterMouseClick: true
        })
    }

    function handleSearchInputKeyDown(event: any) {
        const keyCode = event?.keyCode
        if (keyCode === 40 && isResultsVisible && combinedResults.length > 0) {
            setFocusedResultIndex(0)
            event.preventDefault()
        }
    }

    function handleResultKeyDown(event: any, index: number) {
        event.preventDefault()
        const keyCode = event?.keyCode

        if (keyCode === 40) {
            setFocusedResultIndex(index !== combinedResults.length - 1 ? index + 1 : 0)
        }

        if (keyCode === 38) {
            setFocusedResultIndex(index !== 0 ? index - 1 : -1)
        }

        if (keyCode === 37 && isPathSelected) {
            setFocusedResultIndex(-2)
        }
    }

    function handlePathPositionInputKeyDown(event: any) {
        const keyCode = event.keyCode
        if (keyCode === 13) {
            const raw = (pathPositionInputValue ?? "").toString().trim()

            if (!raw) {
                const pathId = selectedPathMeta?.pathId
                if (pathId != null) {
                    ; (async () => {
                        await goToPath(pathId)
                    })()
                } else {
                    clearSearchMapArtifacts()
                }
                return
            }

            normalizePathPositionInput(raw)
        } else if (keyCode === 39) {
            setFocusedResultIndex(-1)
        }
    }

    function clearSearch() {
        setSearchInputValue("")
        setSearchQuery("")
        setIsPathSelected(false)
        setSelectedPathMeta(null)
        setPathPositionInputValue("")
        setPathPositionQuery("")
        
        clearSearchMapArtifacts()
    }

    useEffect(() => {
        const handleDocumentClick = (e: any) => {
            if (e.target.id !== "map-searchbar-input") setIsResultsVisible(false)
        }
        document.addEventListener("click", handleDocumentClick)
        return () => document.removeEventListener("click", handleDocumentClick)
    }, [])

    return (
        <div className="map-searchbar-div">
            <div className="map-searchbar-inputs-div">
                {(isSearching || isFetchingWfs) && (
                    <SmallLoader iconColor="black" className={selectedPathMeta ? "with-path-position-input" : ""} />
                )}

                {isPathSelected && (
                    <input
                        id="map-path-position-input"
                        value={pathPositionInputValue}
                        onChange={(e: any) => setPathPositionInputValue(e.target.value)}
                        onFocus={() => setIsResultsVisible(false)}
                        onKeyDown={handlePathPositionInputKeyDown}
                        autoComplete="off"
                        placeholder="Км+"
                    />
                )}

                <input
                    id="map-searchbar-input"
                    value={searchInputValue}
                    onClick={() => setIsResultsVisible(true)}
                    onChange={(e: any) => setSearchInputValue(e.target.value)}
                    onFocus={() => setFocusedResultIndex(-1)}
                    onKeyDown={handleSearchInputKeyDown}
                    autoComplete="off"
                    placeholder="Введите название населённого пункта, название дороги или координаты"
                />

                <button className="map-searchbar-clear-button" onClick={clearSearch}>
                    <img src={clearIconSrc} />
                </button>
            </div>

            {isResultsVisible && (
                <div className="map-searchbar-results-div" onScroll={(event) => event.preventDefault()}>
                    {combinedResults.length === 0 && !!searchQuery && !isSearching && !isFetchingWfs ? (
                        <div className="map-searchbar-no-results">
                            <span>Результатов не найдено</span>
                        </div>
                    ) : (
                        combinedResults.map((searchResult: any, index: any) => (
                            <MapSearchbarResult
                                key={index}
                                searchResult={searchResult}
                                onClick={() => handleResultClick(searchResult, index)}
                                onKeyDown={(event: any) => handleResultKeyDown(event, index)}
                                focused={index === focusedResultIndex}
                                index={index}
                                searchValue={searchQuery}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    )
}
